import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import AppModal from '../common/AppModal';
import DropZone from './DropZone';
import FileMetadataTable, { isFieldVisible } from './FileMetadataTable';
import { filterCategoriesForFile } from './filterCategoriesForFile';
import { UPLOAD_SCHEMAS } from './contentTypeSchemas';
import { useDrupalFileUpload } from '../../hooks/useDrupalFileUpload';
import { useTaxonomyTerms, type TaxonomyTerm } from '../../hooks/useTaxonomyTerms';
import { usePermissions } from '../../context/PermissionContext';
import { PERIMETER_SUBCATEGORY_NAME } from '../../constants/perimeterSource';
import type { StagedFile, FileMetadata } from './types';
import { todayInNwt } from '../../utils/nwtDate';
import WeatherUrlForm, { type WeatherUrlKind } from './WeatherUrlForm';
import './MultiFileUploader.scss';

type Step = 'pick' | 'metadata' | 'uploading' | 'done' | 'url';

interface UploadContext {
  wildfireId?: string;
  varId?: string;
  regionId?: string;
  // Fire number for fire_file uploads; required by the backend when the
  // perimeter subcategory routes through the perimeter pipeline.
  fireNumber?: string;
}

interface MultiFileUploaderProps {
  contentType: 'fire_file' | 'var_photo' | 'weather_file';
  context: UploadContext;
  identifier?: string; // fire number, VAR ID, etc. — shown in the modal title
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
}

let nextId = 0;
function makeId() {
  return `staged-${++nextId}-${Date.now()}`;
}

function validateFile(f: StagedFile, schema: typeof UPLOAD_SCHEMAS[string], subcategories: TaxonomyTerm[]): string[] {
  const errors: string[] = [];
  for (const field of schema.fields) {
    if (!field.required) continue;
    // Conditional fields don't count as required when their visibility
    // condition isn't met for this file.
    if (!isFieldVisible(field, f, subcategories)) continue;
    const value = f.metadata[field.key];
    const stringValue = typeof value === 'string' ? value : '';
    const isEmpty = field.type === 'textarea'
      ? !stringValue.trim()
      : field.type === 'boolean'
        ? false
        : !stringValue;

    if (isEmpty) {
      errors.push(`${field.label} is required`);
    } else if (field.key === 'subcategoryId' && field.filterByExtension && stringValue) {
      const validSubs = filterCategoriesForFile(f.file, subcategories);
      if (!validSubs.find((t) => t.id === stringValue)) {
        errors.push('Subcategory not valid for this file type');
      }
    }
  }
  return errors;
}

const MultiFileUploader: React.FC<MultiFileUploaderProps> = ({
  contentType,
  context,
  identifier,
  isOpen,
  onClose,
  onUploadComplete,
}) => {
  const schema = UPLOAD_SCHEMAS[contentType];
  const [step, setStep] = useState<Step>('pick');
  const [files, setFiles] = useState<StagedFile[]>([]);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  // weather_file only — kind of URL being added (YouTube vs external link)
  // when step === 'url'. Submission wiring lands in stage 2 (see #TBD).
  const [urlKind, setUrlKind] = useState<WeatherUrlKind>('youtube');

  const { uploadFile } = useDrupalFileUpload();
  const { canDo } = usePermissions();
  const {
    terms: subcategoriesRaw,
    loading: subcategoriesLoading,
    error: subcategoriesError,
    refetch: refetchSubcategories,
  } = useTaxonomyTerms(schema.subcategoryVocabulary ?? '');

  // Hide the perimeter subcategory from users who lack the perimeter upload
  // permission — the backend would 403 the dispatch anyway, but showing the
  // option lets people start an upload that's destined to fail.
  const canUploadPerimeter = canDo('wildfires', 'upload_perimeter');
  const subcategories: TaxonomyTerm[] = React.useMemo(() => {
    if (contentType !== 'fire_file' || canUploadPerimeter) return subcategoriesRaw;
    return subcategoriesRaw.filter((t) => t.name !== PERIMETER_SUBCATEGORY_NAME);
  }, [subcategoriesRaw, contentType, canUploadPerimeter]);

  const today = todayInNwt();
  const hasDateField = schema.fields.some((f) => f.key === 'fileDate');

  const handleFilesAccepted = useCallback((accepted: File[]) => {
    const staged: StagedFile[] = accepted.map((file) => ({
      id: makeId(),
      file,
      metadata: hasDateField ? { fileDate: today } : {},
      status: 'staged' as const,
    }));
    setFiles((prev) => [...prev, ...staged]);
    setStep('metadata');
  }, [today, hasDateField]);

  const handleUpdateMetadata = useCallback((id: string, field: keyof FileMetadata, value: string | boolean) => {
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const updated = { ...f, metadata: { ...f.metadata, [field]: value } };
        if (updated.validationErrors?.length) {
          updated.validationErrors = validateFile(updated, schema, subcategories);
        }
        return updated;
      }),
    );
  }, [schema, subcategories]);

  const handleRemove = useCallback((id: string) => {
    setFiles((prev) => {
      const next = prev.filter((f) => f.id !== id);
      if (next.length === 0) setStep('pick');
      return next;
    });
  }, []);

  const handleSubmit = async () => {
    const validated = files.map((f) => ({
      ...f,
      validationErrors: f.status === 'success' ? [] : validateFile(f, schema, subcategories),
    }));
    setFiles(validated);

    const filesToUpload = validated.filter(
      (f) => f.status !== 'success' && f.validationErrors!.length === 0,
    );
    if (filesToUpload.length === 0) return;

    setStep('uploading');
    let successes = successCount;
    let errors = 0;

    for (const staged of filesToUpload) {
      setFiles((prev) =>
        prev.map((f) => (f.id === staged.id ? { ...f, status: 'uploading' as const } : f)),
      );

      try {
        const result = await uploadFile(staged.file, {
          ...staged.metadata,
          // Context wildfire (set by callers like FilesTab where the fire is
          // already known) takes precedence; otherwise honor the per-file
          // wildfireId from the metadata form.
          wildfireId: context.wildfireId ?? (staged.metadata.wildfireId as string | undefined),
          varId: context.varId,
          regionId: context.regionId,
          // Forward the fire number so the backend can route a fire-perimeter
          // subcategory upload into the perimeter pipeline.
          ...(context.fireNumber ? { fire_number: context.fireNumber } : {}),
        }, contentType);

        if (result.success) {
          successes++;
          setFiles((prev) =>
            prev.map((f) => (f.id === staged.id ? { ...f, status: 'success' as const, error: undefined } : f)),
          );
        } else {
          errors++;
          setFiles((prev) =>
            prev.map((f) =>
              f.id === staged.id
                ? { ...f, status: 'error' as const, error: result.error || 'Upload failed' }
                : f,
            ),
          );
        }
      } catch (err) {
        errors++;
        setFiles((prev) =>
          prev.map((f) =>
            f.id === staged.id
              ? { ...f, status: 'error' as const, error: err instanceof Error ? err.message : 'Upload failed' }
              : f,
          ),
        );
      }
    }

    setSuccessCount(successes);
    setErrorCount(errors);
    setStep('done');
  };

  const handleRetryFailed = () => {
    setFiles((prev) =>
      prev.map((f) =>
        f.status === 'error'
          ? { ...f, status: 'staged' as const, error: undefined, validationErrors: undefined }
          : f,
      ),
    );
    setErrorCount(0);
    setStep('metadata');
  };

  const handleClose = () => {
    if (successCount > 0) onUploadComplete();
    setFiles([]);
    setStep('pick');
    setSuccessCount(0);
    setErrorCount(0);
    onClose();
  };

  const handleUploadMore = () => {
    setFiles((prev) => prev.filter((f) => f.status === 'success'));
    setStep('pick');
    setErrorCount(0);
  };

  const pendingFiles = files.filter((f) => f.status !== 'success');
  const hasValidationErrors = files.some((f) => f.validationErrors && f.validationErrors.length > 0);
  const isUploading = step === 'uploading';
  const failedCount = files.filter((f) => f.status === 'error').length;
  const title = schema.title(identifier);

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      width={step === 'pick' ? 480 : 640}
      persistent={isUploading}
    >
      <div className="multi-uploader">
        {step === 'pick' && (
          <>
            <h3 className="multi-uploader__title">{title}</h3>
            <DropZone contentType={contentType} onFilesAccepted={handleFilesAccepted} />
            {contentType === 'weather_file' && (
              <div className="multi-uploader__url-options">
                <div className="multi-uploader__or-divider"><span>or</span></div>
                <div className="multi-uploader__url-buttons">
                  <button
                    type="button"
                    className="multi-uploader__btn multi-uploader__btn--url"
                    onClick={() => { setUrlKind('youtube'); setStep('url'); }}
                  >
                    <FontAwesomeIcon icon={faYoutube} style={{ color: '#c4302b' }} /> Add YouTube
                  </button>
                  <button
                    type="button"
                    className="multi-uploader__btn multi-uploader__btn--url"
                    onClick={() => { setUrlKind('link'); setStep('url'); }}
                  >
                    <FontAwesomeIcon icon={faLink} style={{ color: '#0d6efd' }} /> Add Link
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {step === 'url' && contentType === 'weather_file' && (
          <WeatherUrlForm
            kind={urlKind}
            subcategories={subcategories}
            onCancel={() => setStep('pick')}
            onSuccess={() => {
              // Match the file-upload completion flow: refresh the panel and
              // close the modal. Reset internal state so reopening lands on pick.
              onUploadComplete();
              setStep('pick');
            }}
          />
        )}

        {(step === 'metadata' || step === 'uploading') && (
          <>
            <h3 className="multi-uploader__title">
              {isUploading ? 'Uploading...' : title}
            </h3>

            {step === 'metadata' && schema.subcategoryVocabulary && subcategoriesError && (
              <div className="multi-uploader__banner multi-uploader__banner--error" role="alert">
                Couldn't load subcategories from CMS. Uploads need a subcategory to submit.{' '}
                <button
                  type="button"
                  className="multi-uploader__banner-action"
                  onClick={() => refetchSubcategories()}
                >
                  Retry
                </button>
              </div>
            )}
            {step === 'metadata' && schema.subcategoryVocabulary && subcategoriesLoading && subcategories.length === 0 && (
              <div className="multi-uploader__banner multi-uploader__banner--info">
                Loading subcategories…
              </div>
            )}

            {step === 'metadata' && (
              <div className="multi-uploader__drop-more">
                <DropZone contentType={contentType} onFilesAccepted={handleFilesAccepted} disabled={isUploading} />
              </div>
            )}

            <FileMetadataTable
              files={files}
              fields={schema.fields}
              subcategories={subcategories}
              onUpdateMetadata={handleUpdateMetadata}
              onRemove={handleRemove}
              disabled={isUploading}
            />

            {step === 'metadata' && (
              <div className="multi-uploader__actions">
                <button type="button" className="multi-uploader__btn multi-uploader__btn--cancel" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="multi-uploader__btn multi-uploader__btn--primary"
                  onClick={handleSubmit}
                  disabled={pendingFiles.length === 0 || hasValidationErrors}
                >
                  Upload {pendingFiles.length} File{pendingFiles.length !== 1 ? 's' : ''}
                </button>
              </div>
            )}
          </>
        )}

        {step === 'done' && (
          <>
            <h3 className="multi-uploader__title">Upload Complete</h3>
            <div className="multi-uploader__summary">
              {successCount > 0 && (
                <p className="multi-uploader__summary-success">
                  {successCount} file{successCount !== 1 ? 's' : ''} uploaded
                </p>
              )}
              {failedCount > 0 && (
                <p className="multi-uploader__summary-error">
                  {failedCount} file{failedCount !== 1 ? 's' : ''} failed
                </p>
              )}
            </div>

            <FileMetadataTable
              files={files}
              fields={schema.fields}
              subcategories={subcategories}
              onUpdateMetadata={handleUpdateMetadata}
              onRemove={handleRemove}
              disabled
            />

            <div className="multi-uploader__actions">
              {failedCount > 0 && (
                <button type="button" className="multi-uploader__btn multi-uploader__btn--primary" onClick={handleRetryFailed}>
                  Fix and Retry {failedCount} Failed
                </button>
              )}
              <button type="button" className="multi-uploader__btn multi-uploader__btn--cancel" onClick={handleUploadMore}>
                Upload More
              </button>
              <button type="button" className="multi-uploader__btn multi-uploader__btn--primary" onClick={handleClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </AppModal>
  );
};

export default MultiFileUploader;
